import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import {
    AppBar, Toolbar, Drawer, List, ListItemButton, ListItemIcon, ListItemText, IconButton, Typography,
    Box, Grid2 as Grid, Tooltip, Link
} from '@mui/material';
import {
    Home as HomeIcon, Menu as MenuIcon, Close as CloseIcon, VpnKey as VpnKeyIcon,
    Business as BusinessIcon, AccountTree as AccountTreeIcon, Groups as GroupsIcon, Send as SendIcon
} from '@mui/icons-material';
import API from '@utils/axios';
import { useAuth } from '@contexts/AuthContext';
import AccountMenu from '@components/AccountMenu';
import LangSelect from '@components/LangSelect';
import PackTypeIcon from '@components/pack/PackTypeIcon';

function Layout({ children, withFloatingButton = false }) {
    const { t, i18n } = useTranslation();
    const { user, isAdmin, isSuperAdmin } = useAuth();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [miniDrawer, setMiniDrawer] = useState(
        localStorage.getItem('miniDrawer') === 'true'
    );
    useEffect(() => {
        localStorage.setItem('miniDrawer', miniDrawer);
    }, [miniDrawer]);

    const handleLanguageChange = async (event) => {
        try {
            const data = { lang: event.target.value };
            await API.put('/account', data);
            i18n.changeLanguage(event.target.value);
        } catch (error) {
            // Erreur de changement de langue => tant pis
        }
    };

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
        setMiniDrawer(false);
    };

    const toggleMiniDrawer = () => {
        setMiniDrawer(!miniDrawer);
        setMobileOpen(false);
    };

    const setLocalStorageJson = (key, subKey, value) => {
        if (localStorage.getItem(key)) {
            const data = JSON.parse(localStorage.getItem(key)) || {};
            data[subKey] = value;
            localStorage.setItem(key, JSON.stringify(data));
        }
    };

    const client =
        <div>
            <Typography variant="h2" noWrap>
                {user ? user.getNameAndRole() : ''}
            </Typography>
        </div>;

    const menu =
        <div>
            <Tooltip title={miniDrawer ? t('Home') : ''} placement="right" arrow>
                <Link to="/" component={NavLink} underline="none" color="inherit">
                    <ListItemButton className={location.pathname === '/' ? 'activeMenu' : ''}>
                        <ListItemIcon>
                            <HomeIcon sx={{ fontSize: 26 }} />
                        </ListItemIcon>
                        <ListItemText primary={t('Home')} sx={{ whiteSpace: 'nowrap' }} />
                    </ListItemButton>
                </Link>
            </Tooltip>

            <Tooltip title={miniDrawer ? t('Workflows') : ''} placement="right" arrow>
                <Link to="/workflows/custom" component={NavLink} underline="none" color="inherit">
                    <ListItemButton className={location.pathname.startsWith('/workflows') ? 'activeMenu' : ''}>
                        <ListItemIcon>
                            <AccountTreeIcon sx={{ fontSize: 26 }} />
                        </ListItemIcon>
                        <ListItemText primary={t('Workflows')} sx={{ whiteSpace: 'nowrap' }} />
                    </ListItemButton>
                </Link>
            </Tooltip>

            <Tooltip title={miniDrawer ? t('Mailing lists') : ''} placement="right" arrow>
                <Link to="/mailinglists" component={NavLink} underline="none" color="inherit">
                    <ListItemButton className={location.pathname.startsWith('/mailinglists') ? 'activeMenu' : ''}>
                        <ListItemIcon>
                            <GroupsIcon sx={{ fontSize: 26 }} />
                        </ListItemIcon>
                        <ListItemText primary={t('Mailing lists')} sx={{ whiteSpace: 'nowrap' }} />
                    </ListItemButton>
                </Link>
            </Tooltip>

            <Tooltip title={miniDrawer ? t('Sendouts') : ''} placement="right" arrow>
                <Link to="/sendouts" component={NavLink} underline="none" color="inherit">
                    <ListItemButton className={location.pathname === '/sendouts' ? 'activeMenu' : ''}>
                        <ListItemIcon>
                            <SendIcon sx={{ fontSize: 26 }} />
                        </ListItemIcon>
                        <ListItemText primary={t('Sendouts')} sx={{ whiteSpace: 'nowrap' }} />
                    </ListItemButton>
                </Link>
            </Tooltip>

            {!isSuperAdmin() &&
                <>
                    <Tooltip title={miniDrawer ? t('{{type}} credits', { type: t('credit.sms') }) : ''} placement="right" arrow>
                        <Link to="/packs/sms" component={NavLink} underline="none" color="inherit">
                            <ListItemButton className={location.pathname.startsWith('/packs/sms') ? 'activeMenu' : ''}>
                                <ListItemIcon>
                                    <PackTypeIcon type="sms" sx={{ fontSize: 26 }} />
                                </ListItemIcon>
                                <ListItemText primary={t('{{type}} credits', { type: t('credit.sms') })} sx={{ whiteSpace: 'nowrap' }} />
                            </ListItemButton>
                        </Link>
                    </Tooltip>
                    <Tooltip title={miniDrawer ? t('{{type}} credits', { type: t('credit.email') }) : ''} placement="right" arrow>
                        <Link to="/packs/email" component={NavLink} underline="none" color="inherit">
                            <ListItemButton className={location.pathname.startsWith('/packs/email') ? 'activeMenu' : ''}>
                                <ListItemIcon>
                                    <PackTypeIcon type="email" sx={{ fontSize: 26 }} />
                                </ListItemIcon>
                                <ListItemText primary={t('{{type}} credits', { type: t('credit.email') })} sx={{ whiteSpace: 'nowrap' }} />
                            </ListItemButton>
                        </Link>
                    </Tooltip>
                </>
            }

            {isAdmin() &&
                <Tooltip title={miniDrawer ? t('Users') : ''} placement="right" arrow>
                    <Link to="/users" component={NavLink} underline="none" color="inherit"
                        onClick={() => {
                            if (!location.pathname.startsWith('/users')) {
                                setLocalStorageJson('users.pagination', 'page', 0);
                                localStorage.removeItem('users.searchVisible');
                                localStorage.removeItem('users.searchValues');
                            }
                        }}>
                        <ListItemButton className={location.pathname.startsWith('/users') ? 'activeMenu' : ''}>
                            <ListItemIcon>
                                <VpnKeyIcon sx={{ fontSize: 26 }} />
                            </ListItemIcon>
                            <ListItemText primary={t('Users')} sx={{ whiteSpace: 'nowrap' }} />
                        </ListItemButton>
                    </Link>
                </Tooltip>
            }

            {isSuperAdmin() &&
                <Tooltip title={miniDrawer ? t('Customers') : ''} placement="right" arrow>
                    <Link to="/customers" component={NavLink} underline="none" color="inherit"
                        onClick={() => {
                            if (!location.pathname.startsWith('/customers')) {
                                setLocalStorageJson('customers.pagination', 'page', 0);
                                localStorage.removeItem('customers.searchVisible');
                                localStorage.removeItem('customers.searchValues');
                            }
                        }}>
                        <ListItemButton className={location.pathname.startsWith('/customers') ? 'activeMenu' : ''}>
                            <ListItemIcon>
                                <BusinessIcon sx={{ fontSize: 26 }} />
                            </ListItemIcon>
                            <ListItemText primary={t('Customers')} sx={{ whiteSpace: 'nowrap' }} />
                        </ListItemButton>
                    </Link>
                </Tooltip>
            }

            <LangSelect
                emptyLabel={null}
                short={miniDrawer}
                size="small"
                value={i18n.language}
                onChange={handleLanguageChange}
                displayEmpty
                inputProps={{ 'aria-label': 'Language' }}
                sx={{ ml: miniDrawer ? 1 : 2, mt: 2, width: miniDrawer ? 'auto' : '86%' }}
                variant={miniDrawer ? 'standard' : 'outlined'}
            />

        </div>
        ;

    return (
        <div>
            <Box sx={{ display: 'flex' }}>

                {/* Menu Smartphone */}
                <Drawer
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    variant="temporary"
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        width: 240,
                        zIndex: 100,
                        [`& .MuiDrawer-paper`]: { width: 240, boxSizing: 'border-box' },
                    }}
                >
                    <List sx={{ mt: { xs: 9, sm: 6 } }}>
                        {menu}
                    </List>
                </Drawer>

                {/* Menu Desktop */}
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        transition: 'width 0.3s ease',
                        width: miniDrawer ? 59 : 240,
                        zIndex: 100,
                        [`& .MuiDrawer-paper`]: {
                            transition: 'width 0.3s ease',
                            width: miniDrawer ? 59 : 240,
                            boxSizing: 'border-box'
                        },
                    }}
                >
                    <List sx={{ mt: 6 }}>
                        {menu}
                    </List>
                </Drawer>

                <AppBar position="fixed" elevation={0} variant="outlined" sx={{ backgroundColor: 'background.paper', color: 'text.primary' }}>
                    <Toolbar variant="dense">
                        {/* Icone Menu Smartphone */}
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="menu"
                            onClick={handleDrawerToggle}
                            sx={{ display: { xs: 'block', md: 'none' } }}
                        >
                            {mobileOpen ? <CloseIcon /> : <MenuIcon />}
                        </IconButton>

                        {/* Icone Menu Desktop */}
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="menu"
                            onClick={toggleMiniDrawer}
                            sx={{ display: { xs: 'none', md: 'block' } }}
                        >
                            {mobileOpen ? <CloseIcon /> : <MenuIcon />}
                        </IconButton>

                        {/* Logo + client Smartphone */}
                        <Grid
                            container
                            spacing={1}
                            direction="column"
                            alignItems="flex-start"
                            sx={{ display: { xs: 'flex', sm: 'none', flexGrow: 1 } }}
                        >
                            <Grid>
                                <img src="/logo.webp" alt="Logo" style={{ width: 188, height: 'auto' }} />
                            </Grid>
                            <Grid>
                                <Typography component="h1" variant="h2" sx={{ ml: 1.5, mt: -3 }}>
                                    {client}
                                </Typography>
                            </Grid>
                        </Grid>


                        {/* Logo + client Desktop */}
                        <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', flexGrow: 1 }}>
                            <img src="/logo.webp" alt="Logo" style={{ width: 188, height: 'auto' }} />
                            <Box sx={{ ml: { md: 3 } }}>
                                {client}
                            </Box>
                        </Box>

                        <AccountMenu sx={{ ml: 2 }} />
                    </Toolbar>
                </AppBar>


                <Box component="main"
                    sx={{
                        flexGrow: 1,
                        px: 3,
                        pb: (withFloatingButton ? 17 : 1),
                        my: { xs: 4, sm: 1 }
                    }}>
                    <Toolbar />
                    {children}
                </Box>

            </Box>
        </div>
    );
}

export default Layout;